import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Calendar, Clock, MapPin, MessageSquare, CheckCircle, XCircle, AlertCircle, Eye, Filter, BarChart3 } from 'lucide-react';
import OutingNavigation from './OutingNavigation';

const StaffOutingPortal = () => {
  const { user } = useAuth();
  const [outingRequests, setOutingRequests] = useState([]);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showDenialModal, setShowDenialModal] = useState(false);
  const [activeTab, setActiveTab] = useState('pending');
  const [filterStatus, setFilterStatus] = useState('all');

  // Approval/Denial form state
  const [approvalData, setApprovalData] = useState({
    message: '',
    isApproved: true
  });

  useEffect(() => {
    if (user) {
      fetchOutingRequests();
      fetchPendingApprovals();
    }
  }, [user]);

  const fetchOutingRequests = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/outing/requests', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setOutingRequests(data.outingRequests || []);
      }
    } catch (error) {
      console.error('Error fetching outing requests:', error);
      setError('Failed to fetch outing requests');
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingApprovals = async () => {
    try {
      const response = await fetch('/api/outing/pending-approvals', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setPendingRequests(data.pendingRequests || []);
      }
    } catch (error) {
      console.error('Error fetching pending approvals:', error);
    }
  };

  const handleApproval = async () => {
    if (!selectedRequest) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`/api/outing/request/${selectedRequest._id}/approve`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(approvalData)
      });

      if (response.ok) {
        const result = await response.json();
        // Update the request in both lists
        setOutingRequests(prev => 
          prev.map(req => 
            req._id === selectedRequest._id ? result.outingRequest : req
          )
        );
        setPendingRequests(prev => 
          prev.filter(req => req._id !== selectedRequest._id)
        );
        setShowApprovalModal(false);
        setSelectedRequest(null);
        setApprovalData({ message: '', isApproved: true });
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to approve request');
      }
    } catch (error) {
      console.error('Error approving request:', error);
      setError('Failed to approve request');
    } finally {
      setLoading(false);
    }
  };

  const handleDenial = async () => {
    if (!selectedRequest) return;

    setLoading(true);
    setError('');

    try {
      const response = await fetch(`/api/outing/request/${selectedRequest._id}/deny`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(approvalData)
      });

      if (response.ok) {
        const result = await response.json();
        // Update the request in both lists
        setOutingRequests(prev => 
          prev.map(req => 
            req._id === selectedRequest._id ? result.outingRequest : req
          )
        );
        setPendingRequests(prev => 
          prev.filter(req => req._id !== selectedRequest._id)
        );
        setShowDenialModal(false);
        setSelectedRequest(null);
        setApprovalData({ message: '', isApproved: false });
      } else {
        const errorData = await response.json();
        setError(errorData.message || 'Failed to deny request');
      }
    } catch (error) {
      console.error('Error denying request:', error);
      setError('Failed to deny request');
    } finally {
      setLoading(false);
    }
  };

  const openApprovalModal = (request) => {
    setSelectedRequest(request);
    setApprovalData({ message: '', isApproved: true });
    setShowApprovalModal(true);
  };

  const openDenialModal = (request) => {
    setSelectedRequest(request);
    setApprovalData({ message: '', isApproved: false });
    setShowDenialModal(true);
  };

  const canApprove = (request) => {
    if (!user || !user.staffDetails) return false;
    
    const userType = user.staffDetails.designation;
    const currentStatus = request.approvalStatus;
    
    switch (userType) {
      case 'class_teacher':
        return currentStatus === 'pending' && request.parentPermission?.isGranted;
      case 'warden':
        return currentStatus === 'class_teacher_approved';
      case 'principal':
        return currentStatus === 'warden_approved';
      default:
        return false;
    }
  };

  const canDeny = (request) => {
    return canApprove(request);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'class_teacher_approved': return 'bg-blue-100 text-blue-800';
      case 'warden_approved': return 'bg-indigo-100 text-indigo-800';
      case 'principal_approved': return 'bg-green-100 text-green-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'denied': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending': return 'Pending Parent Permission';
      case 'class_teacher_approved': return 'Class Teacher Approved';
      case 'warden_approved': return 'Warden Approved';
      case 'principal_approved': return 'Principal Approved';
      case 'approved': return 'Approved';
      case 'denied': return 'Denied';
      default: return 'Unknown';
    }
  };

  const getFilteredRequests = () => {
    if (activeTab === 'pending') {
      return pendingRequests;
    }
    
    let filtered = outingRequests;
    if (filterStatus !== 'all') {
      filtered = filtered.filter(req => req.approvalStatus === filterStatus);
    }
    return filtered;
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  const filteredRequests = getFilteredRequests();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Navigation */}
      <OutingNavigation userRole="staff" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Message */}
        <div className="mb-6">
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <div className="flex items-center space-x-3">
              <Calendar className="h-6 w-6 text-purple-600" />
              <div>
                <h3 className="text-lg font-medium text-purple-900">
                  Welcome, {user.profile?.firstName || 'Staff'} ({user.staffDetails?.designation || 'Staff'})!
                </h3>
                <p className="text-purple-700">
                  Review and approve student outing requests here.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Page Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Outing Management</h2>
              <p className="text-gray-600">Review and approve student outing requests</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => {/* TODO: Open statistics modal */}}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center"
              >
                <BarChart3 className="h-4 w-4 mr-2" />
                Statistics
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8">
              <button
                onClick={() => setActiveTab('pending')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'pending'
                    ? 'border-teal-500 text-teal-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Pending Approvals ({pendingRequests.length})
              </button>
              <button
                onClick={() => setActiveTab('all')}
                className={`py-2 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'all'
                    ? 'border-teal-500 text-teal-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                All Requests
              </button>
            </nav>
          </div>
        </div>

        {/* Filters */}
        {activeTab === 'all' && (
          <div className="mb-6">
            <div className="flex items-center space-x-4">
              <Filter className="h-5 w-5 text-gray-400" />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
              >
                <option value="all">All Statuses</option>
                <option value="pending">Pending</option>
                <option value="class_teacher_approved">Class Teacher Approved</option>
                <option value="warden_approved">Warden Approved</option>
                <option value="principal_approved">Principal Approved</option>
                <option value="approved">Approved</option>
                <option value="denied">Denied</option>
              </select>
            </div>
          </div>
        )}

        {/* Outing Requests List */}
        <div className="bg-white rounded-lg shadow-md">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              {activeTab === 'pending' ? 'Pending Approvals' : 'All Outing Requests'}
            </h3>
          </div>

          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Loading outing requests...</p>
            </div>
          ) : filteredRequests.length === 0 ? (
            <div className="p-8 text-center">
              <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">
                {activeTab === 'pending' ? 'No pending approvals' : 'No outing requests found'}
              </p>
              <p className="text-sm text-gray-500">
                {activeTab === 'pending' 
                  ? 'All requests have been processed' 
                  : 'No requests match the current filters'
                }
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-200">
              {filteredRequests.map((request) => (
                <div key={request._id} className="p-6 hover:bg-gray-50">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-3">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(request.approvalStatus)}`}>
                          {getStatusText(request.approvalStatus)}
                        </span>
                        <span className="text-sm text-gray-500">
                          Request ID: {request.requestId}
                        </span>
                        {request.parentPermission?.isGranted && (
                          <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                            Parent Permission Granted
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                        <div className="flex items-center space-x-2">
                          <Calendar className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {new Date(request.outingDate).toLocaleDateString()}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Clock className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {request.departureTime} - {request.returnTime}
                          </span>
                        </div>
                        <div className="flex items-center space-x-2">
                          <MapPin className="h-4 w-4 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {request.numberOfDays} day{request.numberOfDays > 1 ? 's' : ''}
                          </span>
                        </div>
                      </div>

                      {request.studentMessage && (
                        <div className="flex items-start space-x-2 mb-3">
                          <MessageSquare className="h-4 w-4 text-gray-400 mt-0.5" />
                          <p className="text-sm text-gray-600">{request.studentMessage}</p>
                        </div>
                      )}

                      {/* Approval Flow Status */}
                      <div className="mt-4">
                        <h4 className="text-sm font-medium text-gray-700 mb-2">Approval Status:</h4>
                        <div className="flex space-x-4">
                          <div className="flex items-center space-x-2">
                            <div className={`w-3 h-3 rounded-full ${
                              request.approvalFlow?.classTeacher?.status === 'approved' ? 'bg-green-500' : 
                              request.approvalFlow?.classTeacher?.status === 'denied' ? 'bg-red-500' : 'bg-gray-300'
                            }`}></div>
                            <span className="text-xs text-gray-600">Class Teacher</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className={`w-3 h-3 rounded-full ${
                              request.approvalFlow?.warden?.status === 'approved' ? 'bg-green-500' : 
                              request.approvalFlow?.warden?.status === 'denied' ? 'bg-red-500' : 'bg-gray-300'
                            }`}></div>
                            <span className="text-xs text-gray-600">Warden</span>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div className={`w-3 h-3 rounded-full ${
                              request.approvalFlow?.principal?.status === 'approved' ? 'bg-green-500' : 
                              request.approvalFlow?.principal?.status === 'denied' ? 'bg-red-500' : 'bg-gray-300'
                            }`}></div>
                            <span className="text-xs text-gray-600">Principal</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2">
                      {canApprove(request) && (
                        <button
                          onClick={() => openApprovalModal(request)}
                          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm flex items-center"
                        >
                          <CheckCircle className="h-4 w-4 mr-2" />
                          Approve
                        </button>
                      )}
                      {canDeny(request) && (
                        <button
                          onClick={() => openDenialModal(request)}
                          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm flex items-center"
                        >
                          <XCircle className="h-4 w-4 mr-2" />
                          Deny
                        </button>
                      )}
                      <button
                        onClick={() => {/* TODO: View details modal */}}
                        className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                        title="View Details"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Approval Modal */}
      {showApprovalModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Approve Outing Request</h3>
              <p className="text-sm text-gray-600">Approve this student's outing request</p>
            </div>

            <div className="px-6 py-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Message (Optional)
                </label>
                <textarea
                  value={approvalData.message}
                  onChange={(e) => setApprovalData({...approvalData, message: e.target.value})}
                  rows="3"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Any additional comments or instructions..."
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowApprovalModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleApproval}
                  disabled={loading}
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Approving...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Approve
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Denial Modal */}
      {showDenialModal && selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Deny Outing Request</h3>
              <p className="text-sm text-gray-600">Deny this student's outing request</p>
            </div>

            <div className="px-6 py-4">
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for Denial *
                </label>
                <textarea
                  value={approvalData.message}
                  onChange={(e) => setApprovalData({...approvalData, message: e.target.value})}
                  rows="3"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-teal-500"
                  placeholder="Please provide a reason for denying this request..."
                  required
                />
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              )}

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowDenialModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDenial}
                  disabled={loading || !approvalData.message.trim()}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Denying...
                    </>
                  ) : (
                    <>
                      <XCircle className="h-4 w-4 mr-2" />
                      Deny
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffOutingPortal;
